import { Capacitor } from '@capacitor/core';

import { applyRequestHeaders, responseErrorMessage } from '../http.ts';

export type AuthenticationPurpose = 'login' | 'confirm';

export type AuthenticationResult =
    | {
          state: 'two-factor';
          challenge: string;
          purpose: AuthenticationPurpose;
      }
    | {
          state: 'complete';
          grant: string;
          purpose: AuthenticationPurpose;
      };

export type PasskeyOptions<TOptions extends object> = {
    challenge: string;
    options: TOptions;
};

let csrfReady: Promise<void> | undefined;

export class AuthenticationRequestError extends Error {
    constructor(
        public response: Response,
        public data: any,
    ) {
        const message =
            responseErrorMessage(response, data) || `Something went wrong (${response.status})`;

        super(message);
    }
}

async function responseData(response: Response) {
    const text = await response.text();

    if (!text) {
        return undefined;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

async function request(url: string, method = 'GET', body?: Record<string, unknown>) {
    const headers = applyRequestHeaders(new Headers({ Accept: 'application/json' }));

    if (body) {
        headers.set('Content-Type', 'application/json');
    }

    const options: RequestInit = {
        method,
        headers,
        credentials: 'same-origin',
    };

    if (body) {
        options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await responseData(response);

    if (!response.ok) {
        throw new AuthenticationRequestError(response, data);
    }

    return { response, data };
}

async function ensureCsrf() {
    if (Capacitor.isNativePlatform()) {
        return;
    }

    const pending = (csrfReady ??= request('/sanctum/csrf-cookie').then(() => undefined));

    try {
        await pending;
    } finally {
        if (csrfReady === pending) {
            csrfReady = undefined;
        }
    }
}

export async function authRequest<T>(
    path: string,
    method: 'DELETE' | 'GET' | 'POST' = 'POST',
    body?: Record<string, unknown>,
): Promise<T> {
    if (method !== 'GET') {
        await ensureCsrf();
    }

    const apiUrl = Capacitor.isNativePlatform() ? import.meta.env.VITE_API_URL : '/api';
    const result = await request(`${apiUrl}/auth/${path}`, method, body);

    if (result.response.status === 204) {
        return undefined as T;
    }

    if (result.data === undefined) {
        throw new Error('The authentication request did not return a response.');
    }

    return result.data as T;
}
